import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { API_LOGIN } from '../Config/ApiUrlConstant';
import CryptoJS from 'crypto-js';
import { AUTH_ENCRYPTION_KEY } from '../utils/auth';
import { apiJson } from '../utils/apiClient';
import './LoginForm.css';

const BankIcon = () => <svg viewBox="0 0 64 64" role="img" aria-label="Banking portal"><path d="M7 24 32 9l25 15H7Z"/><path d="M12 28h40M15 28v20m11-20v20m12-20v20m11-20v20M9 52h46"/></svg>;
const UserIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="3.5"/><path d="M5 20c.5-4 2.8-6 7-6s6.5 2 7 6"/></svg>;
const LockIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10" width="14" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>;

export default function LoginForm() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsSubmitting(true);
      setError('');
      const encryptedUsername = CryptoJS.AES.encrypt(username, AUTH_ENCRYPTION_KEY).toString();
      const encryptedPassword = CryptoJS.AES.encrypt(password, AUTH_ENCRYPTION_KEY).toString();
      const { response, data } = await apiJson(API_LOGIN, {
        method: 'POST',
        body: JSON.stringify({ username: encryptedUsername, password: encryptedPassword }),
      });
      if (response.ok && data?.token) login(data.token, data.user, data.permissions || []);
      else setError(data?.message || 'Unable to sign in.');
    } catch (requestError) {
      setError('Unable to reach the banking service. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return <main className="bank-login-layout">
    <section className="bank-login-story">
      <div className="bank-login-brand"><span><BankIcon /></span><div><strong>Thirdwave Banking</strong><small>Operations workspace</small></div></div>
      <div className="bank-login-copy"><p>Connected financial operations</p><h1>Banking work,<br /><em>clearly managed.</em></h1><span>Manage clients, lending, collections, branches, and operational reports from one controlled workspace.</span></div>
      <div className="bank-login-visual" aria-hidden="true"><div className="bank-visual-glow"/><div className="bank-building"><div className="bank-roof"/><div className="bank-columns"><i/><i/><i/><i/></div><div className="bank-base"/></div><div className="bank-flow bank-flow-one"><b>₹</b><span>Collections</span></div><div className="bank-flow bank-flow-two"><b>↗</b><span>Portfolio</span></div><div className="bank-flow bank-flow-three"><b>✓</b><span>Approvals</span></div></div>
      <div className="bank-login-capabilities"><span>Branch operations</span><span>Loan portfolio</span><span>Repayment control</span></div>
    </section>
    <section className="bank-login-panel"><div className="bank-login-card">
      <div className="bank-login-mobile-mark"><BankIcon /></div><p className="bank-login-kicker">Secure banking access</p><h2>Welcome back.</h2><p className="bank-login-help">Sign in with your authorized operations account.</p>
      {error && <div className="bank-login-error" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} className="bank-login-form">
        <label htmlFor="bank-username">Username<div className="bank-login-input"><UserIcon/><input id="bank-username" type="text" value={username} onChange={(event)=>setUsername(event.target.value)} autoComplete="username" placeholder="Enter your username" autoFocus required/></div></label>
        <label htmlFor="bank-password">Password<div className="bank-login-input"><LockIcon/><input id="bank-password" type={showPassword?'text':'password'} value={password} onChange={(event)=>setPassword(event.target.value)} autoComplete="current-password" placeholder="Enter your password" required/><button type="button" className="bank-password-toggle" onClick={()=>setShowPassword((current)=>!current)} aria-label={showPassword?'Hide password':'Show password'}>{showPassword?'Hide':'Show'}</button></div></label>
        <button type="submit" className="bank-login-submit" disabled={isSubmitting}>{isSubmitting?<><i/>Signing in…</>:<>Sign in to workspace <span>→</span></>}</button>
      </form><p className="bank-login-security"><i/>Access is protected by role, branch, and action permissions.</p>
    </div></section>
  </main>;
}
